<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import toPrice from '~/utils/toPrice'
import Button from '~/components/Button.vue'
import PackageAdditionalServiceEnum from '~/enums/PackageAdditionalServiceEnum'
import lockScroll from '~/utils/lockScroll'
import { useLang } from '~/composables/useLang'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'

const { isMobile } = useStoreConfig()
const { t } = useLang()

const contentStore = useSiteProContentStore()

const formNotes = ref('')
const formNotesError = ref<string[]>([])

const props = defineProps<{
  visible: boolean
  itemId: number | null
}>()

const visible = ref<Boolean>(props.visible)
const lastPosition = ref(0)

watch(
  () => props.visible,
  (isVisible) => {
    visible.value = isVisible
    if (isVisible) {
      lastPosition.value = window?.scrollY ?? 0
    }
    lockScroll(isVisible, lastPosition.value)
  }
)
const emit = defineEmits(['handle', 'success'])

function handle(value: boolean) {
  emit('handle', value)
}

async function sendServiceRequest() {
  formNotes.value = formNotes.value.trim()
  if (formNotes.value.length < 1) {
    formNotesError.value = [t('validation.required')]

    return
  }

  const serviceId = PackageAdditionalServiceEnum.InspectPackage

  await $fetch('/api/site-pro/submit-additional-service', {
    method: 'POST',
    body: {
      serviceId,
      itemId: props.itemId,
      instructions: formNotes.value,
    },
    ...fetchRetryParameters,
    headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
  })

  formNotesError.value = []
  formNotes.value = ''

  emit('success', props.itemId, serviceId)
  visible.value = false
}
</script>

<template>
  <lazy-client-only>
    <Modal
      :visible="visible"
      class="modal-component modal-md"
      custom-header
      :class="{ 'modal-mobile': isMobile }"
      @handle="handle">
      <template #header>
        <div class="flex items-center gap-2">
          <h5 class="text-text1 dark:text-dmText1">{{ t('site_pro.additional_services.inspect_package.title') }}</h5>
          <div class="px-[10px] py-[5px] bg-neutral7 dark:bg-dmGray3 border border-neutral5 rounded-md">
            <h6>{{ toPrice(contentStore.additionalServicesConfig?.inspect_package.price) }}</h6>
          </div>
        </div>
      </template>
      <div class="modal-component-body">
        <div class="pb-6">{{ t('site_pro.additional_services.inspect_package.modal_description') }}</div>
        <FormKit
          v-model="formNotes"
          name="notes"
          :placeholder="t('site_pro.additional_services.inspect_package.textarea_placeholder')"
          :label="t('form.notes.label')"
          type="textarea"
          :errors="formNotesError" />
        <div class="flex gap-4 pt-1">
          <Button class="w-full" type="submit" @click="sendServiceRequest">
            {{ t('common.submit') }}
          </Button>
          <Button class="w-full" button-type="secondary" type="submit" @click="handle(false)">
            {{ t('common.close') }}
          </Button>
        </div>
      </div>
    </Modal>
  </lazy-client-only>
</template>
